import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from 'axios'

type CfgWithRetry = InternalAxiosRequestConfig & { _retry?: boolean }

// ---- Base URL (Vite/browser-safe) ----
const BASE =
  (typeof import.meta !== 'undefined' && (import.meta as ImportMeta).env?.VITE_API_URL) ||
  'https://server-yiksi-website.on-forge.com/' //update

const api: AxiosInstance = axios.create({
  baseURL: String(BASE).replace(/\/+$/, ''),
  withCredentials: false, // using Bearer tokens, not cookies
})

// ---- Token utils ----
const getLS = (k: string) => (typeof localStorage !== 'undefined' ? localStorage.getItem(k) || '' : '')
const getAccess = () => getLS('access_token')
const getRefresh = () => getLS('refresh_token')

export const applyTokens = (access?: string, refresh?: string) => {
  if (typeof localStorage === 'undefined') return
  if (access) localStorage.setItem('access_token', access)
  if (refresh) localStorage.setItem('refresh_token', refresh)
}
export const clearTokens = () => {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

// Avoid sending Authorization to these endpoints
const isAuthPath = (url?: string) =>
  !!url &&
  (/\/auth\/login(\?|$)/.test(url) ||
    /\/auth\/register(\?|$)/.test(url) ||
    /\/auth\/refresh(\?|$)/.test(url) ||
    /\/auth\/logout(\?|$)/.test(url))

// ---------- REQUEST ----------
api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  if (isAuthPath(cfg.url || '')) return cfg
  const t = getAccess()
  if (t) {
    cfg.headers = cfg.headers || new AxiosHeaders()
    ;(cfg.headers as AxiosHeaders).set('Authorization', `Bearer ${t}`)
  }
  return cfg
})

// ---------- RESPONSE / REFRESH ----------
let refreshing = false
let queue: Array<(token: string | null) => void> = []
const flushQueue = (token: string | null) => {
  queue.forEach((cb) => cb(token))
  queue = []
}

api.interceptors.response.use(
  (r: AxiosResponse) => r,
  async (err: AxiosError) => {
    const res = err.response
    const cfg = err.config as CfgWithRetry | undefined

    if (res?.status === 404 && cfg) {
      // eslint-disable-next-line no-console
      console.error('404 at:', `${cfg.baseURL || ''}${cfg.url || ''}`)
    }

    // Bubble up 401s that come from auth endpoints themselves
    if (res?.status === 401 && cfg && isAuthPath(cfg.url || '')) {
      return Promise.reject(err)
    }

    // Try refresh once for other 401s
    if (res?.status === 401 && cfg && !cfg._retry) {
      cfg._retry = true

      if (!refreshing) {
        refreshing = true
        try {
          // raw axios (no interceptors) to refresh
          const { data } = await axios.post<{ access_token: string; refresh_token?: string }>(
            `${api.defaults.baseURL}/auth/refresh`,
            {},
            { headers: { Authorization: `Bearer ${getRefresh()}` } }
          )

          const newAccess = data?.access_token
          if (!newAccess) throw new Error('No access_token from refresh')

          applyTokens(newAccess, data?.refresh_token)
          refreshing = false
          flushQueue(newAccess)
        } catch (e) {
          refreshing = false
          clearTokens()
          flushQueue(null)
          return Promise.reject(e)
        }
      }

      // Queue the current request until refresh completes
      return new Promise((resolve, reject) => {
        queue.push((token: string | null) => {
          if (!token) return reject(new Error('Unauthorized'))
          const retryCfg: CfgWithRetry = { ...cfg }
          retryCfg.headers = AxiosHeaders.from(cfg.headers || {})
          retryCfg.headers.set('Authorization', `Bearer ${token}`)
          resolve(api(retryCfg))
        })
      })
    }

    return Promise.reject(err)
  }
)

export default api

// Optional: helper your store can call on logout
export function resetAuthClient() {
  clearTokens()
  // no default Authorization header set on instance; nothing else needed
}